import React, { useEffect, useState } from 'react'

import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    TextField,
    useTheme,
} from '@material-ui/core'

import { Container, Selector, Swiper, TimelinePicker } from '../../components'
import {
    Area,
    Issue,
    ListVendor,
    Schedule,
    Service,
    User,
    WorkorderType,
} from '../../models'
import { AssignUnitDict, CreateWorkOrderRequest } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import { fromPickerDate } from '../../helpers'
import { useTimezone } from '../../contexts/timezone'

interface Props {
    open: boolean
    serviceList: Service[]
    vendorList: ListVendor[]
    userList: User[]
    issues: Issue[]
    loading: boolean
    scheduleList: Schedule[]
    initialTitle?: string
    areas: Area[]
    // Dictionary mapping issue ID to Area object
    issueAreaMap?: Record<number, Area>
    handleClose: () => void
    onClickCreate: (request: any) => void
}

const ASSIGN_TO_USER = false
const ASSIGN_TO_COMPANY = true

export const IssueWorkorderModal = (props: Props) => {
    const {
        open,
        serviceList,
        vendorList,
        userList,
        issues,
        initialTitle,
        scheduleList,
        loading,
        areas,
        issueAreaMap,
        handleClose,
        onClickCreate,
    } = props

    const theme = useTheme()
    const { currentTimezone } = useTimezone()

    // Form state
    const [title, setTitle] = useState(initialTitle || '')
    const [priority, setPriority] = useState(false)
    const [doValidate, setDoValidate] = useState(false)
    const [selectedScheduleId, setSelectedScheduleId] = useState(-1)

    // Assignment state
    const [swiperState, setSwiperState] = useState({
        position: ASSIGN_TO_COMPANY,
        userId: -1,
    })

    // Local state for service and vendor selection
    const [selectedServiceId, setSelectedServiceId] = useState(-1)
    const [selectedVendorId, setSelectedVendorId] = useState(-1)

    // Local state for dates to avoid hook issues
    const [startDate, setStartDateLocal] = useState(new Date())
    const [endDate, setEndDateLocal] = useState(new Date())

    // Show all vendors for now to avoid filtering issues
    const filteredVendorList = vendorList

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open) {
            setTitle(initialTitle || '')
            setPriority(false)
            setSwiperState({
                position: ASSIGN_TO_COMPANY,
                userId: -1,
            })
            setDoValidate(false)
            setSelectedServiceId(-1)
            setSelectedVendorId(-1)
            setStartDateLocal(new Date())
            setEndDateLocal(new Date())
            setSelectedScheduleId(-1)
        }
    }, [open, initialTitle])

    const handleCreate = () => {
        if (title === '') {
            setDoValidate(true)
            return
        }

        const startDateIso = fromPickerDate(startDate, currentTimezone)
            .startOf('day')
            .toUTC()
            .toISO()
        const endDateIso = fromPickerDate(endDate, currentTimezone)
            .endOf('day')
            .toUTC()
            .toISO()

        const request: CreateWorkOrderRequest = {
            body: {
                title: title,
                workorder_type:
                    selectedScheduleId === -1
                        ? WorkorderType.ACTION_ITEM
                        : WorkorderType.SERVICE_ORDER,
                start_date: startDateIso ?? undefined,
                end_date: endDateIso ?? undefined,
                assign_to_type: swiperState.position ? 'user' : 'vendor',
                assign_to_id: swiperState.position
                    ? swiperState.userId
                    : selectedVendorId,
                issue_ids: issues.map((issue) => issue.id),
            },
        }

        const units: AssignUnitDict[] = []
        const issueIds: number[] = []

        // Map each issue to its specific unit and area
        if (issueAreaMap) {
            issues.forEach((issue) => {
                const issueArea = issueAreaMap[issue.id]
                if (issueArea) {
                    units.push({
                        unit_id: issueArea.unit,
                        service_areas: [issueArea.id],
                    })
                    issueIds.push(issue.id)
                }
            })
        } else {
            // Fallback to the old logic if no area map is provided
            const unitId: number = areas[0]?.unit || 0
            const serviceAreas: number[] = areas.map((area) => area.id)

            issues.forEach((issue) => {
                units.push({
                    unit_id: unitId,
                    service_areas: serviceAreas,
                })
                issueIds.push(issue.id)
            })
        }

        request.body.units = units
        request.body.issue_ids = issueIds

        if (priority) {
            request.body.priority = true
        }

        if (selectedServiceId !== -1) {
            request.body.service = selectedServiceId
        }

        if (selectedScheduleId !== -1) {
            request.body.schedule = selectedScheduleId
        }

        onClickCreate(request)
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper style={{ display: 'flex', width: 800 }}>
                    <Container style={{ flexDirection: 'column', flex: 1 }}>
                        {/* Header */}
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                                color: theme.palette.grey[800],
                                margin: theme.spacing(1),
                            }}
                        >
                            Create Workorders from Issues
                        </span>

                        {/* Title Field */}
                        <TextField
                            label="Workorder Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            style={{ margin: theme.spacing(1) }}
                            variant="outlined"
                            error={doValidate && title === ''}
                        />

                        {/* Date Range Picker */}
                        <div style={{ margin: theme.spacing(1) }}>
                            <TimelinePicker
                                startDate={startDate}
                                endDate={endDate}
                                onChangeStartDate={setStartDateLocal}
                                onChangeEndDate={setEndDateLocal}
                            />
                        </div>

                        {/* Service Selector */}
                        <Selector
                            label="Service"
                            data={serviceList}
                            searchable
                            currentValue={selectedServiceId}
                            onChange={(e) =>
                                setSelectedServiceId(e.target.value as number)
                            }
                            getDisplayString={(service) => service.name}
                            customStyle={{
                                formControl: { margin: theme.spacing(1) },
                            }}
                        />

                        {/* Project Selector */}
                        <Selector
                            label="Project"
                            data={scheduleList}
                            searchable
                            currentValue={selectedScheduleId}
                            onChange={(e) =>
                                setSelectedScheduleId(e.target.value as number)
                            }
                            getDisplayString={(schedule) => schedule.name}
                            customStyle={{
                                formControl: { margin: theme.spacing(1) },
                            }}
                        />

                        {/* Assignment Switcher */}
                        <Container
                            style={{
                                flexDirection: 'column',
                                margin: theme.spacing(1),
                                border: `1px solid ${theme.palette.grey[500]}`,
                                borderRadius: theme.shape.borderRadius,
                            }}
                        >
                            <Swiper
                                width={775}
                                style={{ overflow: 'hidden', margin: 4 }}
                                position={swiperState.position}
                                onChangePosition={() => {
                                    setSwiperState({
                                        ...swiperState,
                                        position: !swiperState.position,
                                    })
                                }}
                                leftItem={
                                    <FormControl fullWidth>
                                        <span
                                            style={{
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                            }}
                                        >
                                            My Vendors
                                        </span>
                                        <Select
                                            value={selectedVendorId}
                                            placeholder="My Vendors"
                                            label="My Vendors"
                                            onChange={(e) =>
                                                setSelectedVendorId(
                                                    Number(e.target.value),
                                                )
                                            }
                                        >
                                            <MenuItem
                                                key="NO_VENDOR_SELECTED"
                                                value={-1}
                                            >
                                                <span
                                                    style={{
                                                        color:
                                                            theme.palette
                                                                .grey[700],
                                                    }}
                                                >
                                                    Not Assigned (My Vendors)
                                                </span>
                                            </MenuItem>
                                            {filteredVendorList.map((vnd) => (
                                                <MenuItem
                                                    key={`SELECT_VENDOR_${vnd.id}`}
                                                    value={vnd.id}
                                                >
                                                    {vnd.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }
                                rightItem={
                                    <FormControl fullWidth>
                                        <span
                                            style={{
                                                fontWeight:
                                                    theme.typography
                                                        .fontWeightBold,
                                            }}
                                        >
                                            My Team
                                        </span>
                                        <Select
                                            value={swiperState.userId}
                                            label="My Team"
                                            placeholder="My Team"
                                            onChange={(e) => {
                                                setSwiperState({
                                                    ...swiperState,
                                                    userId: Number(
                                                        e.target.value,
                                                    ),
                                                })
                                            }}
                                        >
                                            <MenuItem
                                                key="NO_USER_SELECTED"
                                                value={-1}
                                            >
                                                <span
                                                    style={{
                                                        color:
                                                            theme.palette
                                                                .grey[700],
                                                    }}
                                                >
                                                    Not Assigned (My Team)
                                                </span>
                                            </MenuItem>
                                            {userList.map((user) => (
                                                <MenuItem
                                                    key={`SELECT_USER_${user.id}`}
                                                    value={user.id}
                                                >
                                                    {user.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }
                            />
                        </Container>

                        {/* Priority Checkbox */}
                        <Container style={{ marginLeft: theme.spacing(1) }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={priority}
                                        onChange={() => setPriority(!priority)}
                                    />
                                }
                                label="Priority"
                            />
                        </Container>

                        <Divider style={{ marginTop: theme.spacing(1) }} />

                        {/* Action Buttons */}
                        <Container
                            style={{
                                alignItems: 'center',
                                padding: theme.spacing(1),
                            }}
                        >
                            <Container style={{ flex: 1 }} />
                            <Button
                                onClick={handleClose}
                                variant="contained"
                                style={{
                                    textTransform: 'none',
                                    width: '100px',
                                }}
                                color="secondary"
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={loading}
                                variant="contained"
                                style={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: loading
                                        ? '#fff'
                                        : theme.palette.primary.contrastText,
                                    textTransform: 'none',
                                    marginLeft: theme.spacing(1),
                                    width: '100px',
                                }}
                                onClick={handleCreate}
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={25}
                                        style={{ color: 'white' }}
                                    />
                                ) : (
                                    <span
                                        style={{
                                            fontWeight:
                                                theme.typography.fontWeightBold,
                                        }}
                                    >
                                        {`Create (${issues.length})`}
                                    </span>
                                )}
                            </Button>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
